import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  Box,
  IconButton,
  Snackbar,
  Alert,
  InputLabel
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const EditAd = () => {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [adData, setAdData] = useState(null);
  const [images, setImages] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const adRef = doc(db, 'ads', adId);
        const adSnap = await getDoc(adRef);
        if (adSnap.exists()) {
          const data = adSnap.data();
          setAdData(data);
          setImages(data.images || []);
          setCoverIndex(data.coverPhotoIndex || 0);
        }
      } catch (err) {
        setError('Грешка при преземање на податоци.');
      }
    };
    fetchAd();
  }, [adId]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const filePath = `cars/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, filePath);
    const uploadTask = uploadBytesResumable(fileRef, file);
    uploadTask.on(
      'state_changed',
      null,
      (err) => {
        console.error(err);
        setError('Грешка при прикачување.');
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        const updatedImages = [...images, url];
        setImages(updatedImages);
        setUploading(false);
        // Auto-save images
        await updateDoc(doc(db, 'ads', adId), {
          images: updatedImages
        });
      }
    );
  };

  const handleImageRemove = async (index) => {
    const toDelete = images[index];
    try {
      const imageRef = ref(storage, toDelete);
      await deleteObject(imageRef);
    } catch (err) {
      console.warn('Не можеше да се избрише сликата од storage:', err);
    }
    const newImages = [...images];
    newImages.splice(index, 1);
    let newCover = coverIndex;
    if (index === coverIndex) newCover = 0;
    else if (index < coverIndex) newCover = coverIndex - 1;
    setImages(newImages);
    setCoverIndex(newCover);
    await updateDoc(doc(db, 'ads', adId), {
      images: newImages,
      coverPhotoIndex: newCover
    });
  };

  const handleSave = async () => {
    try {
      const adRef = doc(db, 'ads', adId);
      await updateDoc(adRef, {
        price: parseFloat(adData.price),
        mileage: parseInt(adData.mileage),
        description: adData.description,
        contactNumber: adData.contactNumber,
        whatsappNumber: adData.whatsappNumber,
        viberNumber: adData.viberNumber,
        images,
        coverPhotoIndex: coverIndex
      });
      setSuccess(true);
      setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      setError('Неуспешно зачувување.');
    }
  };

  const handleCoverClick = async (idx) => {
    setCoverIndex(idx);
    try {
      await updateDoc(doc(db, 'ads', adId), {
        coverPhotoIndex: idx
      });
    } catch (err) {
      console.error('Грешка при зачувување на насловната слика', err);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(images);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    let newCover = coverIndex;
    if (result.source.index === coverIndex) {
      newCover = result.destination.index;
    } else if (result.source.index < coverIndex && result.destination.index >= coverIndex) {
      newCover = coverIndex - 1;
    } else if (result.source.index > coverIndex && result.destination.index <= coverIndex) {
      newCover = coverIndex + 1;
    }

    setImages(reordered);
    setCoverIndex(newCover);

    try {
      await updateDoc(doc(db, 'ads', adId), {
        images: reordered,
        coverPhotoIndex: newCover
      });
    } catch (err) {
      console.error('Грешка при автоматско зачувување', err);
    }
  };

  if (!adData) return <Typography align="center">Се вчитува...</Typography>;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom align="center">
          Уреди оглас
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Цена (€)"
              type="number"
              fullWidth
              value={adData.price || ''}
              onChange={(e) => setAdData({ ...adData, price: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Километража (км)"
              type="number"
              fullWidth
              value={adData.mileage || ''}
              onChange={(e) => setAdData({ ...adData, mileage: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Опис"
              fullWidth
              multiline
              rows={4}
              value={adData.description || ''}
              onChange={(e) => setAdData({ ...adData, description: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Телефон"
              fullWidth
              value={adData.contactNumber || ''}
              onChange={(e) => setAdData({ ...adData, contactNumber: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="WhatsApp"
              fullWidth
              value={adData.whatsappNumber || ''}
              onChange={(e) => setAdData({ ...adData, whatsappNumber: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Viber"
              fullWidth
              value={adData.viberNumber || ''}
              onChange={(e) => setAdData({ ...adData, viberNumber: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel sx={{ mb: 1 }}>Слики (редослед + насловна)</InputLabel>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable-images" direction="horizontal">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}
                  >
                    {images.map((img, idx) => (
                      <Draggable key={img} draggableId={img} index={idx}>
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{ position: 'relative' }}
                          >
                            <img
                              src={img}
                              alt={`car-${idx}`}
                              style={{
                                width: 100,
                                height: 70,
                                borderRadius: 8,
                                border: idx === coverIndex ? '3px solid green' : '1px solid #ccc',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleCoverClick(idx)}
                            />
                            <IconButton
                              size="small"
                              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                              onClick={() => handleImageRemove(idx)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    <Button
                      component="label"
                      variant="outlined"
                      disabled={uploading}
                    >
                      + Додади
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" onClick={handleSave} disabled={uploading}>
                Зачувај промени
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" variant="filled">
          Успешно зачувано
        </Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditAd;
